import { useState } from 'react';
import { Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { BiTrash, BiErrorCircle, BiShow, BiGitBranch, BiCopyAlt } from 'react-icons/bi';
import useDeleteExpense from '../../hooks/useDeleteExpense';
import { formatDate, formatCurrency } from '../../utils/formatters';
import ExpenseDetailModal from './expenseDetailModal';
import AccountingStatusPill from '../integrations/AccountingStatusPill';

// Matches FieldConfidenceFloor on ReceiptScanWorker (server). Anything under
// this is rendered amber + tagged "please check" — and was the same threshold
// the worker's tier-3 trigger uses, so a row without a pill never had a tier-3
// retry available to clear it.
const CONFIDENCE_FLOOR = 0.85;
const isLowConf = (conf, key) => conf != null && conf[key] != null && conf[key] < CONFIDENCE_FLOOR;
const lowConfClass = (conf, key) => isLowConf(conf, key) ? 'text-warning fw-semibold' : '';

export default function ExpenseRow({ expense, currency = 'GBP' }) {
    // Each expense carries its own currency now (shared by its splits); fall
    // back to the job currency prop for older payloads.
    const rowCurrency = expense.currency || currency;
    const deleteExpense = useDeleteExpense();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    // Paid & locked in the accounting package → can't be deleted (server also
    // enforces this; the disabled button just avoids the error round-trip).
    const locked = expense.accountingStatus === 'locked';

    const handleConfirmDelete = () => {
        // DeleteExpense cascades on the server: the controller loops the
        // attached receipts, deletes each blob, then the DB FK cascade
        // removes the Receipt rows alongside the Expense. No per-receipt
        // UI needed here because the relation is 1:1.
        deleteExpense.mutate(expense.id, {
            onSuccess: () => setConfirmOpen(false),
        });
    };

    // OcrFieldConfidence comes from the worker as { supplier: 0.92, ... };
    // any field below CONFIDENCE_FLOOR earns an amber treatment + the row
    // header gets a "Please check" pill so the user knows to verify before
    // the expense locks in. Manual-entry expenses don't carry this map and
    // never show the pill.
    const ocrConf = expense.ocrFieldConfidence;
    const hasLowConf = ocrConf != null
        && Object.values(ocrConf).some(v => v != null && v < CONFIDENCE_FLOOR);

    const subtitle = [
        { text: formatDate(expense.expenseDate), key: 'expenseDate' },
        { text: expense.categoryName, key: 'category' },
        { text: expense.supplier, key: 'supplier' },
    ].filter(part => part.text);

    // When this expense is split across jobs, the headline number is THIS
    // job's share (allocatedAmount), with the full receipt amount shown as
    // a quieter "of £X total" suffix. Row-level header makes it obvious the
    // user is looking at a shared receipt.
    const isSplit = Array.isArray(expense.splits) && expense.splits.length > 0;
    const headlineAmount = isSplit && expense.allocatedAmount != null
        ? expense.allocatedAmount
        : expense.amount;
    const headlineVat = isSplit && expense.allocatedVatAmount != null
        ? expense.allocatedVatAmount
        : expense.vatAmount;

    return (
        <div className="surface p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="flex-grow-1">
                    <div className="d-flex align-items-baseline gap-3 flex-wrap">
                        <strong className={lowConfClass(ocrConf, 'amount')}>
                            {formatCurrency(headlineAmount, rowCurrency)}
                        </strong>
                        {headlineVat != null && (
                            <span className={`text-muted small ${lowConfClass(ocrConf, 'vatAmount')}`}>
                                VAT {formatCurrency(headlineVat, rowCurrency)}
                            </span>
                        )}
                        {isSplit && (
                            <Badge color="info" pill className="d-inline-flex align-items-center gap-1"
                                   title={`Receipt total ${formatCurrency(expense.amount, rowCurrency)} split across ${expense.splits.length} jobs`}>
                                <BiGitBranch /> Split · your share of {formatCurrency(expense.amount, rowCurrency)}
                            </Badge>
                        )}
                        {hasLowConf && (
                            <Badge color="warning" pill className="d-inline-flex align-items-center gap-1">
                                <BiErrorCircle /> Please check
                            </Badge>
                        )}
                        {expense.possibleDuplicate && (
                            <Badge color="warning" pill className="d-inline-flex align-items-center gap-1"
                                   title="A similar expense already exists. Open to review or dismiss.">
                                <BiCopyAlt /> Possible duplicate
                            </Badge>
                        )}
                        <AccountingStatusPill status={expense.accountingStatus} />
                    </div>
                    {subtitle.length > 0 && (
                        <div className="text-muted small">
                            {subtitle.map((part, i) => (
                                <span key={i} className={part.key ? lowConfClass(ocrConf, part.key) : ''}>
                                    {i > 0 && ' · '}{part.text}
                                </span>
                            ))}
                        </div>
                    )}
                    {expense.description && (
                        <div className="mt-1">{expense.description}</div>
                    )}
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                    <Button color="primary" outline size="sm"
                            onClick={() => setDetailOpen(true)}
                            className="d-inline-flex align-items-center gap-1">
                        <BiShow /> View
                    </Button>
                    <Button color="danger" outline size="sm"
                            onClick={() => setConfirmOpen(true)}
                            disabled={locked}
                            title={locked ? 'Paid & locked in your accounting package — can’t be deleted' : 'Delete'}
                            className="d-inline-flex align-items-center gap-1">
                        <BiTrash />
                    </Button>
                </div>
            </div>

            <ExpenseDetailModal
                isOpen={detailOpen}
                onClose={() => setDetailOpen(false)}
                expense={expense}
                currency={rowCurrency}
            />

            <Modal isOpen={confirmOpen}
                   toggle={() => !deleteExpense.isPending && setConfirmOpen(false)}
                   centered backdrop="static">
                <ModalHeader toggle={() => !deleteExpense.isPending && setConfirmOpen(false)}>
                    Delete expense?
                </ModalHeader>
                <ModalBody>
                    <p className="mb-2">
                        Permanently delete this expense for {formatCurrency(expense.amount, rowCurrency)}?
                    </p>
                    <p className="text-muted mb-0 small">
                        This also deletes the attached receipt. This cannot be undone.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setConfirmOpen(false)}
                            disabled={deleteExpense.isPending}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleConfirmDelete}
                            disabled={deleteExpense.isPending}>
                        {deleteExpense.isPending && <Spinner size="sm" className="me-1" />}
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
